import { z } from 'zod';

/**
 * Configuration d'exécution injectée par /env.js (public/env.js en dev, template envsubst en image).
 * Une seule image pour tous les environnements : rien n'est figé au build.
 */
const RuntimeEnvSchema = z.object({
  /** Le backend sert tout sous /api/v1 : `/api` seul répond 404 (mesuré 2026-09-25). */
  apiUrl: z.string().min(1).default('/api/v1'),
  oidcBaseUrl: z.string().default(''),
  /** clientId public de la console dans chaque realm — `graal-ui` (dev/keycloak/README.md §3). */
  oidcClientId: z.string().min(1).default('graal-ui'),
  mcpUrl: z.string().default(''),
  hostingLabel: z.string().default(''),
  hostingCountry: z.string().default('FR'),
  defaultTenant: z.string().default(''),
  /**
   * Le modèle de facturation du déploiement (ADR 0089) : la MÊME valeur que `GRAAL_BILLING_MODE` de graal-api (le
   * chart les dérive toutes deux de `billing.mode`). `self-hosted` par défaut, comme l'application ; seule la page
   * « Abonnement » en dépend (un masque : le backend répond 501 en `saas`).
   */
  billingMode: z
    .string()
    .default('self-hosted')
    .transform((value): 'saas' | 'self-hosted' =>
      value.trim().toLowerCase() === 'saas' ? 'saas' : 'self-hosted',
    ),
  /**
   * Mocks MSW : '1' branche le service worker MSW **et** le fournisseur d'authentification
   * local — aucun appel réseau ne sort. Toute autre valeur = vraie API + vrai Keycloak
   * (`GRAAL_LIVE=1 pnpm dev` en développement, `GRAAL_MOCKS=` vide en image).
   */
  mocks: z
    .union([z.string(), z.boolean()])
    .default('')
    .transform((value) => value === true || value === '1' || value === 'true'),
  /**
   * Fournisseur d'authentification, indépendant des mocks d'API :
   * `oidc` = vrai Keycloak, `mock` = session locale. Vide = déduit de `mocks`.
   * Les deux sont séparés pour pouvoir vérifier le vrai flow OIDC contre un Keycloak local
   * alors que `graal-api` (donc le lookup tenant → realm) n'existe pas encore.
   */
  auth: z.enum(['oidc', 'mock']).or(z.literal('')).default(''),
});

export type RuntimeEnv = z.infer<typeof RuntimeEnvSchema>;

declare global {
  interface Window {
    __GRAAL_ENV__?: Partial<Record<keyof RuntimeEnv, string>>;
  }
}

let cached: RuntimeEnv | undefined;

export function runtimeEnv(): RuntimeEnv {
  if (!cached) {
    const parsed = RuntimeEnvSchema.safeParse(
      typeof window === 'undefined' ? {} : (window.__GRAAL_ENV__ ?? {}),
    );
    cached = parsed.success ? parsed.data : RuntimeEnvSchema.parse({});
  }
  return cached;
}

export function authMode(): 'oidc' | 'mock' {
  const env = runtimeEnv();
  return env.auth === '' ? (env.mocks ? 'mock' : 'oidc') : env.auth;
}

/** Tests uniquement : rejoue la lecture de window.__GRAAL_ENV__. */
export function resetRuntimeEnv(): void {
  cached = undefined;
}
